import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, takeUntil, tap } from 'rxjs';
import { TooltipPosition } from 'src/app/shared/components/tooltip/tooltip.enums';
import { SimulationsBySupplier } from 'src/app/shared/objects/simulations-by-supplier';
import { PricingFlag, WholesalerSimulation } from 'src/app/shared/objects/wholesaler-simulation';
import { WholesalerSimulationGlobalInformation } from 'src/app/shared/objects/wholesaler-simulation-global-information';

import { CustomNumberFormatPipe } from 'src/app/shared/pipe/custom-number-format.pipe';
import { CommonModule } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-wholesaler-table',
  standalone: true,
  imports: [
    FormsModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatInputModule,
    CustomNumberFormatPipe,
    MatTooltipModule,
    MatTabsModule,
    CommonModule,
    MatIconModule,
  ],
  templateUrl: './wholesaler-table.component.html',
  styleUrls: ['./wholesaler-table.component.scss'],
})
export class WholesalerTableComponent implements OnInit, OnDestroy {
  @Input({ required: true }) wholesalerSimulation!: SimulationsBySupplier<
    WholesalerSimulation,
    WholesalerSimulationGlobalInformation
  >;

  msgTooltipAssiette = 'Regroupement des produits des lignes de factures';
  msgTooltipQuantite = 'Quantité de produit dans l’assiette';
  msgTooltipMontantBrutHT = 'Montant brut HT de l’assiette présent sur la facture';
  msgTooltipMontantBrutHTPF = 'Montant brut HT en prix fabricant (source BCB)';
  msgTooltipRSFEuro = 'Remise sur facture en euros de l’assiette';
  msgTooltipRSFPercent = 'Calcul en % de la remise = (RSF € / Montant brut HT) * 100';
  msgTooltipMontantNetHT = 'Montant brut HT - RSF €';
  msgTooltipSimulationIndividuelle =
    'Valeur en % à simuler (s’applique au Montant brut HT PF ou au Montant brut HT facturé en fonction de la définition de l’assiette)';
  msgTooltipSimulationIndividuelleBoite =
    'Valeur de remise en € à simuler (s’applique à la quantité)';
  msgTooltipTotalSimulation =
    "Simulation à percevoir au total = (Simulation individuelle en %/100) * (Montant brut HT PF ou Montant brut HT facturé selon l'assiette) OU (Simulation individuelle à la boîte * Quantité)";
  msgTooltipSimulationRecevoir = 'Différence entre le Total Simulation € et RSF € déjà perçue';

  msgTooltipTotalPercu = 'Saisie du montant déjà perçu s’il y en a';
  msgTooltipResteARecevoir = 'Différence entre Simulation à recevoir et Total perçu';

  private readonly _destroying$ = new Subject<void>();
  private readonly _targetPercentageChange$ = new Subject<WholesalerSimulation>();
  private readonly _amountCollectedChange$ = new Subject<number>();

  sortDirection: 'asc' | 'desc' = 'asc';
  protected readonly TooltipPosition = TooltipPosition;
  private _amountCollected = 0;
  private _globalTotalCooperation = 0;
  private _globalNetTotalSimulation = 0;

  get amountToCollect(): number {
    return this._globalNetTotalSimulation - this._amountCollected;
  }

  get amountCollected(): number {
    return this._amountCollected;
  }

  get globalTotalCooperation(): number {
    return this._globalTotalCooperation;
  }

  get globalNetTotalSimulation(): number {
    return this._globalNetTotalSimulation;
  }

  modifiedAmountCollected = (): boolean => this._amountCollected !== 0;

  ngOnInit(): void {
    this._targetPercentageChange$
      .pipe(
        takeUntil(this._destroying$),
        tap(simulation => {
          this.calculateBaseTotalCooperation(simulation);
          this.calculateDiscountPercent(simulation);
        }),
        tap(() => {
          this.calculateGlobalTotalCooperation();
          this.calculateNetTotalSimulation();
        }),
      )
      .subscribe();

    this._amountCollectedChange$
      .pipe(
        takeUntil(this._destroying$),
        tap(amount => (this._amountCollected = amount)),
      )
      .subscribe();
    // Calcul du % de RSF pour toutes les simulations au démarrage
    if (this.wholesalerSimulation?.simulations) {
      this.wholesalerSimulation.simulations.forEach(sim => {
        this.calculateDiscountPercent(sim);
      });
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  focusNextInput(event: Event): void {
    event.preventDefault();
    const currentElement = event.target as HTMLElement;
    const formElements = Array.from(
      document.querySelectorAll<HTMLElement>('input, select, textarea, button, [tabindex]'),
    ).filter(el => !el.hasAttribute('disabled') && !el.getAttribute('tabindex')?.includes('-1'));

    const currentIndex = formElements.indexOf(currentElement);
    if (currentIndex !== -1 && currentIndex + 1 < formElements.length) {
      formElements[currentIndex + 1].focus();
    }
  }

  isPercentileTargetDisabled(base: WholesalerSimulation): boolean {
    return base.grossAmountExclTax === 0;
  }

  onTargetPercentageChange(simulation: WholesalerSimulation, event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = this.parseLocalizedNumber(input.value);
    if (value !== 0) {
      simulation.grossPercentileTarget = value;
      this._targetPercentageChange$.next(simulation);
    }
  }

  onTargetPercentageBlur(simulation: WholesalerSimulation, event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = this.parseLocalizedNumber(input.value);
    // if no input, we reset the input.value to "-" and don't start any simulation
    if (value == 0) {
      input.value = '-';

      // re initialize the value in the base object
      simulation.totalCooperation = 0;
      simulation.netTotalSimulation = 0;

      // relance les calculs
      this.calculateGlobalTotalCooperation(); // TOTAL "total Simulation €"
      this.calculateNetTotalSimulation(); // TOTAL "Simulation à recevoir"
      simulation.grossPercentileTarget = undefined;
      return;
    }
  }

  onCooperationBoxChange(simulation: WholesalerSimulation, event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = this.parseLocalizedNumber(input.value);
    if (value !== 0) {
      simulation.cooperationTargetBox = value;
      this._targetPercentageChange$.next(simulation);
    }
  }

  onCooperationBoxBlur(simulation: WholesalerSimulation, event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = this.parseLocalizedNumber(input.value);
    // if no input, we reset the input.value to "-" and don't start any simulation
    if (value == 0) {
      input.value = '-';

      // re initialize the value in the base object
      simulation.totalCooperation = 0;
      simulation.netTotalSimulation = 0;

      // relance les calculs
      this.calculateGlobalTotalCooperation(); // TOTAL "total Simulation €"
      this.calculateNetTotalSimulation(); // TOTAL "Simulation à recevoir"
      simulation.cooperationTargetBox = undefined;
      return;
    }
  }

  onAmountCollectedBlur(event: Event): void {
    this._amountCollected = this.parseLocalizedNumber((event.target as HTMLInputElement).value);
    this._amountCollectedChange$.next(this._amountCollected);
  }

  getBaseNetAmountExclTax(simulation: WholesalerSimulation): number {
    const calcNetAmount =
      (simulation.grossAmountExclTax ?? 0) - (simulation.discountEuroExclTax ?? 0);

    if (calcNetAmount < 0) {
      return 0;
    }

    return calcNetAmount;
  }

  /**
   * Retourne les lignes avec une simulation saisie (simulation individuelle % ou à la boîte)
   */
  getSimulatedRows() {
    return this.sortedSimulations.filter(
      base =>
        (base.grossPercentileTarget && base.grossPercentileTarget !== 0) ||
        (base.cooperationTargetBox && base.cooperationTargetBox !== 0),
    );
  }

  getSimulatedTotalQuantity() {
    return this.getSimulatedRows().reduce((sum, base) => sum + (base.quantity ?? 0), 0);
  }

  getSimulatedTotalGrossAmountExclTax() {
    return this.getSimulatedRows().reduce((sum, base) => sum + (base.grossAmountExclTax || 0), 0);
  }

  getSimulatedTotalGrossManufacturerAmountExclTax() {
    return this.getSimulatedRows().reduce(
      (sum, base) => sum + (base.grossManufacturerAmountExclTax || 0),
      0,
    );
  }

  getSimulatedTotalDiscountableEuroExclTax() {
    return this.getSimulatedRows().reduce((sum, base) => sum + (base.discountEuroExclTax || 0), 0);
  }

  getSimulatedTotalDiscountPercent() {
    const totalDiscount = this.getSimulatedTotalDiscountableEuroExclTax();
    const totalGross = this.getSimulatedTotalGrossAmountExclTax();
    return totalGross ? (totalDiscount / totalGross) * 100 : 0;
  }

  getSimulatedTotalNetAmountExclTax() {
    return this.getSimulatedRows().reduce(
      (sum, base) => sum + (this.getBaseNetAmountExclTax(base) || 0),
      0,
    );
  }

  getSimulatedTotalCooperation() {
    return this.getSimulatedRows().reduce((sum, base) => sum + (base.totalCooperation || 0), 0);
  }

  getSimulatedNetTotalSimulation() {
    return this.getSimulatedRows().reduce((sum, base) => sum + (base.netTotalSimulation || 0), 0);
  }

  getSimulatedIndividualPercent() {
    const totalSimulation = this.getSimulatedTotalCooperation();
    const totalGross = this.getSimulatedTotalGrossManufacturerAmountExclTax();
    return totalGross ? (totalSimulation / totalGross) * 100 : 0;
  }

  getTotalQuantity(): number {
    return this.wholesalerSimulation.simulations.reduce((acc, d) => acc + (d.quantity ?? 0), 0);
  }

  getTotalGrossAmountExclTax(): number {
    return this.wholesalerSimulation.simulations.reduce(
      (acc, d) => acc + (d.grossAmountExclTax ?? 0),
      0,
    );
  }

  getTotalGrossManufacturerAmountExclTax(): number {
    return this.wholesalerSimulation.simulations.reduce(
      (acc, d) => acc + (d.grossManufacturerAmountExclTax ?? 0),
      0,
    );
  }

  getTotalDiscountableEuroExclTax(): number {
    return this.wholesalerSimulation.simulations.reduce(
      (acc, d) => acc + (d.discountEuroExclTax ?? 0),
      0,
    );
  }

  getTotalNetAmountExclTax(): number {
    return this.wholesalerSimulation.simulations.reduce(
      (acc, d) => acc + (this.getBaseNetAmountExclTax(d) ?? 0),
      0,
    );
  }

  getYearlyNetTurnover(): number {
    return this.getGlobalSumValue('Montant total net remisable');
  }
  getAverageMonthlyNetTurnover(): number {
    return this.getGlobalAverageValue('CA moyen net mensuel');
  }
  getYearlyGrossTurnover(): number {
    return this.getGlobalSumValue('Montant total brut remisable');
  }

  getAverageMonthlyGrossTurnover(): number {
    return this.getGlobalAverageValue('CA moyen brut mensuel');
  }

  private getGlobalSumValue(label: string): number {
    // on calcule la somme totale pour ce label
    return (
      this.wholesalerSimulation.simulationGlobalInformation
        .filter(d => d.calculationName === label)
        .reduce((sum, item) => sum + Number(item.calculationResult ?? 0), 0) || 0
    );
  }

  private getGlobalAverageValue(label: string): number {
    // on calcule la moyenne mensuelle pour ce label
    const filteredData = this.wholesalerSimulation.simulationGlobalInformation.filter(
      d => d.calculationName === label,
    );
    return (
      filteredData.reduce((sum, item) => sum + Number(item.calculationResult ?? 0), 0) /
        filteredData.length || 0
    );
  }
  getYearlyCreditAmountHT(): number {
    return this.getGlobalSumValue('Montant HT Avoir');
  }
  getCreditToInvoiceNetHTRatio(): number {
    const totalAvoirHT = this.getYearlyCreditAmountHT();
    const totalFactureHT = this.getTotalNetAmountExclTax();
    if (totalFactureHT === 0) {
      return 0; // éviter une division par zéro
    }
    return totalAvoirHT / totalFactureHT;
  }

  private calculateDiscountPercent(simulation: WholesalerSimulation): void {
    if (simulation.grossManufacturerAmountExclTax && simulation.discountEuroExclTax) {
      simulation.discountPercent =
        (simulation.discountEuroExclTax / simulation.grossManufacturerAmountExclTax) * 100;
    } else {
      simulation.discountPercent = 0;
    }
  }

  private calculateBaseTotalCooperation(simulation: WholesalerSimulation): void {
    if ((simulation.grossPercentileTarget ?? 0) !== 0) {
      // PP → Montant brut HT facturé ; PF ou absent → Montant brut HT PF (défaut)
      const baseAmount: number | undefined =
        simulation.flagPrix === ('PP' as PricingFlag)
          ? simulation.grossAmountExclTax
          : simulation.grossManufacturerAmountExclTax;
      if (baseAmount && simulation.grossPercentileTarget) {
        simulation.totalCooperation = (simulation.grossPercentileTarget / 100) * baseAmount;
      } else {
        simulation.totalCooperation = 0;
      }
    } else {
      // Mode coopération à la boîte
      simulation.totalCooperation =
        simulation.cooperationTargetBox && simulation.quantity
          ? simulation.cooperationTargetBox * simulation.quantity
          : 0;
    }

    if (simulation.discountEuroExclTax !== undefined) {
      simulation.netTotalSimulation = simulation.totalCooperation - simulation.discountEuroExclTax;
    } else {
      simulation.netTotalSimulation = simulation.totalCooperation;
    }
  }

  private calculateGlobalTotalCooperation(): void {
    this._globalTotalCooperation = this.wholesalerSimulation.simulations.reduce(
      (acc, d) => acc + (d.totalCooperation ?? 0),
      0,
    );
  }
  private calculateNetTotalSimulation(): void {
    this._globalNetTotalSimulation = this.wholesalerSimulation.simulations.reduce(
      (acc, d) => acc + (d.netTotalSimulation ?? 0),
      0,
    );
  }

  private parseLocalizedNumber(value: string): number {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      value = value.replace(/ /g, '');
      const commaIndex = value.indexOf(',');
      if (commaIndex !== -1) {
        value = value.replace(/\./g, '').replace(',', '.');
      }
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  }

  get sortedSimulations(): WholesalerSimulation[] {
    return [...this.wholesalerSimulation.simulations].sort((a, b) => {
      const orderA = a.ordreAssiette ?? 0;
      const orderB = b.ordreAssiette ?? 0;

      return this.sortDirection === 'asc' ? orderA - orderB : orderB - orderA;
    });
  }
  toggleSortDirection(): void {
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
  }
}
